import { LEARNING_SOURCES, LEARNING_STATUSES } from '../../domain/constants/auditLearnings';

/** @typedef {import('../../domain/entities/AuditLearning.types').ProposedLearning} ProposedLearning */
/** @typedef {import('../../domain/entities/AuditLearning.types').AuditLearningDbRecord} AuditLearningDbRecord */
/** @typedef {import('../../domain/entities/AuditLearning.types').AuditLearning} AuditLearning */
/** @typedef {import('../../domain/entities/AuditLearning.types').NoteTemplateOption} NoteTemplateOption */

/**
 * Factory function to create the use cases for audit learning-related operations.
 *
 * @param {Function} auditSessionActivityCommitLearning - The function to commit learnings, injected
 * from infrastructure layer
 * @param {Function} getAuditLearningsApi - The function to retrieve all learnings for a clinic, injected
 * from infrastructure layer
 * @param {Function} getClinicSessionNoteTemplatesApi - The function to retrieve the clinic's session note
 * templates, injected from infrastructure layer
 * @param {Function} auditSessionActivityUpdateLearningApi - The function to update an existing learning, injected
 * from infrastructure layer
 * @param {Function} auditSessionActivityDeleteLearningApi - The function to delete an existing learning, injected
 * from infrastructure layer
 * @returns {{
 *   commitLearning: Function,
 *   getLearnings: Function,
 *   getSessionNoteTemplates: Function,
 *   createLearning: Function,
 *   createDraftRevision: Function,
 *   updateLearning: Function,
 *   saveLearningScope: Function,
 *   deleteLearning: Function,
 * }} - The use case functions for audit learning operations
 */
// eslint-disable-next-line import/prefer-default-export
export function createAuditLearnings(
  auditSessionActivityCommitLearning,
  getAuditLearningsApi,
  getClinicSessionNoteTemplatesApi,
  auditSessionActivityUpdateLearningApi,
  auditSessionActivityDeleteLearningApi,
) {
  /**
   * Commit a learning to the clinic's learning repository
   * @param {string} clinicId - The ID of the clinic to which the learning should be committed
   * @param {string} learning - The learning content to commit
   * @param {{
   *   sessionActivityType: string,
   *   sessionNoteTemplateId?: string,
   * }} context - The learning context to commit
   * @param {string|null} sessionActivityAuditId - Passing for future use
   */
  async function commitLearning(clinicId, learning, context, sessionActivityAuditId = null) {
    await auditSessionActivityCommitLearning({
      clinicId,
      learning,
      context,
      sessionActivityAuditId,
    });
  }

  /**
   * Retrieve every audit learning for a clinic, with the relationships the moderation table needs.
   *
   * A draft revision and the active learning it replaces are two documents that read as one row apiece,
   * so each active learning is told the id of its open draft. That is what lets the table label it
   * "Active, new draft created" and send Edit to the draft rather than opening a second one.
   *
   * @param {string} clinicId - The clinic ID
   * @returns {Promise<AuditLearning[]>}
   */
  async function getLearnings(clinicId) {
    const [learnings, sessionNoteTemplates] = await Promise.all([
      getAuditLearningsApi(clinicId),
      getClinicSessionNoteTemplatesApi(clinicId),
    ]);

    const templateNamesById = sessionNoteTemplates.reduce((acc, { id, noteTemplateName }) => {
      acc[id] = noteTemplateName;
      return acc;
    }, {});

    const draftRevisionIdByParentId = learnings.reduce((acc, learning) => {
      if (learning.parentId && learning.status === LEARNING_STATUSES.DRAFT) {
        acc[learning.parentId] = learning.id;
      }

      return acc;
    }, {});

    return learnings.map((learning) => ({
      ...learning,
      draftRevisionId: draftRevisionIdByParentId[learning.id] ?? null,
      // An id with no name left is a template that has since been deleted. Showing the id is worse than
      // showing nothing, so it is dropped and the count in the column reflects what is still resolvable.
      sessionNoteTemplateNames: (learning.sessionNoteTemplateIds ?? [])
        .map((templateId) => templateNamesById[templateId])
        .filter(Boolean),
    }));
  }

  /**
   * Retrieve the clinic's session note templates for the scope selector.
   * @param {string} clinicId
   * @returns {Promise<NoteTemplateOption[]>}
   */
  async function getSessionNoteTemplates(clinicId) {
    return getClinicSessionNoteTemplatesApi(clinicId);
  }

  /**
   * Create a manually authored learning.
   *
   * An empty `sessionNoteTemplateIds` is the organization scope, not a missing value — the backend reads
   * the field's presence as "this came from the moderation page".
   *
   * @param {{
   *   clinicId: string,
   *   learning: string,
   *   sessionNoteTemplateIds: string[],
   *   saveAsDraft?: boolean,
   * }} params
   * @returns {Promise<{ id: string }>}
   */
  async function createLearning({
    clinicId,
    learning,
    sessionNoteTemplateIds,
    saveAsDraft = false,
  }) {
    return auditSessionActivityCommitLearning({
      clinicId,
      learning,
      sessionNoteTemplateIds,
      saveAsDraft,
      source: LEARNING_SOURCES.MANUAL,
    });
  }

  /**
   * Park new wording for an active learning as a draft revision of it. The active learning keeps applying
   * to audits until the revision passes verification.
   *
   * @param {{
   *   clinicId: string,
   *   parentId: string,
   *   learning: string,
   *   sessionNoteTemplateIds: string[],
   * }} params
   * @returns {Promise<{ id: string }>}
   */
  async function createDraftRevision({
    clinicId,
    parentId,
    learning,
    sessionNoteTemplateIds,
  }) {
    return auditSessionActivityCommitLearning({
      clinicId,
      learning,
      sessionNoteTemplateIds,
      saveAsDraft: true,
      parentId,
      source: LEARNING_SOURCES.MANUAL,
    });
  }

  /**
   * Update the learning text of an existing audit learning record.
   *
   * Used by the thumbs-down flow, which changes the wording of a learning the model proposed and must not
   * touch either its scope or its provenance.
   *
   * @param {string} clinicId - The clinic ID
   * @param {string} learningId - The audit learning document ID
   * @param {string} learning - The updated learning content
   */
  async function updateLearning(clinicId, learningId, learning) {
    await auditSessionActivityUpdateLearningApi({
      clinicId,
      learningId,
      learning,
    });
  }

  /**
   * Save an edit made on the moderation page: text and scope together, as a draft or verified.
   *
   * A verified save of a draft revision is where the backend swaps it for the active learning it
   * replaces, which is why the caller is told whether that happened.
   *
   * @param {{
   *   clinicId: string,
   *   learningId: string,
   *   learning: string,
   *   sessionNoteTemplateIds: string[],
   *   saveAsDraft?: boolean,
   * }} params
   * @returns {Promise<{ promoted: boolean }>}
   */
  async function saveLearningScope({
    clinicId,
    learningId,
    learning,
    sessionNoteTemplateIds,
    saveAsDraft = false,
  }) {
    const result = await auditSessionActivityUpdateLearningApi({
      clinicId,
      learningId,
      learning,
      sessionNoteTemplateIds,
      saveAsDraft,
      source: LEARNING_SOURCES.MANUAL,
    });

    return { promoted: result?.promoted === true };
  }

  /**
   * Delete an audit learning record completely.
   * @param {string} clinicId - The clinic ID
   * @param {string} learningId - The audit learning document ID
   */
  async function deleteLearning(clinicId, learningId) {
    await auditSessionActivityDeleteLearningApi({
      clinicId,
      learningId,
    });
  }

  return {
    commitLearning,
    getLearnings,
    getSessionNoteTemplates,
    createLearning,
    createDraftRevision,
    updateLearning,
    saveLearningScope,
    deleteLearning,
  };
}
